import {
  pgTable,
  text,
  serial,
  integer,
  varchar,
  timestamp,
  jsonb,
  index,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// ── Workflows + folders ─────────────────────────────────────────────────────
// Visual automation builder data. This DEV-176 slice ships the list +
// folder organization + CRUD; the ReactFlow editor and execution engine
// land in a separate spec-11 ticket. Schema is forward-compatible: triggerConfig
// / nodes / edges are JSONB blobs the editor will populate.

export const workflowFolders = pgTable(
  "workflow_folders",
  {
    id: serial("id").primaryKey(),
    name: text("name").notNull(),
    agencyId: varchar("agency_id", { length: 255 }), // null = platform-wide
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (t) => ({
    agencyIdx: index("idx_workflow_folders_agency").on(t.agencyId),
  })
);

export const insertWorkflowFolderSchema = createInsertSchema(workflowFolders)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    name: z.string().min(1).max(255),
  });

export type WorkflowFolder = typeof workflowFolders.$inferSelect;
export type InsertWorkflowFolder = z.infer<typeof insertWorkflowFolderSchema>;

export const workflows = pgTable(
  "workflows",
  {
    id: serial("id").primaryKey(),
    name: text("name").notNull(),
    folderId: integer("folder_id"), // nullable; "ungrouped" when null
    status: varchar("status", { length: 20 }).notNull().default("draft"), // draft | published
    triggerConfig: jsonb("trigger_config"),  // { triggerId, triggerName, filters: [...] } | null
    nodes: jsonb("nodes"),                    // ReactFlow node array; null until editor lands
    edges: jsonb("edges"),                    // ReactFlow edge array
    agencyId: varchar("agency_id", { length: 255 }),
    createdBy: varchar("created_by", { length: 255 }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (t) => ({
    folderIdx: index("idx_workflows_folder").on(t.folderId),
    statusIdx: index("idx_workflows_status").on(t.status),
    agencyIdx: index("idx_workflows_agency").on(t.agencyId),
  })
);

export const insertWorkflowSchema = createInsertSchema(workflows)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    name: z.string().min(1).max(255),
    status: z.enum(["draft", "published"]).default("draft"),
  });

export type Workflow = typeof workflows.$inferSelect;
export type InsertWorkflow = z.infer<typeof insertWorkflowSchema>;

// ── Workflow runs (execution engine) ────────────────────────────────────────
// A run is created for each trigger firing. Each step records one node in the
// graph being executed (or scheduled to execute, for `wait` actions). The
// scheduler polls steps where status='waiting' AND scheduled_for <= now() to
// resume runs that had been paused.

export const workflowRuns = pgTable(
  "workflow_runs",
  {
    id: serial("id").primaryKey(),
    workflowId: integer("workflow_id").notNull(),
    triggerPayload: jsonb("trigger_payload"),
    status: varchar("status", { length: 20 }).notNull().default("running"), // running | waiting | completed | failed | cancelled
    startedAt: timestamp("started_at").defaultNow().notNull(),
    completedAt: timestamp("completed_at"),
    error: text("error"),
  },
  (t) => ({
    workflowIdx: index("idx_workflow_runs_workflow_id").on(t.workflowId),
    statusIdx: index("idx_workflow_runs_status").on(t.status),
  })
);

export type WorkflowRun = typeof workflowRuns.$inferSelect;

export const workflowRunSteps = pgTable(
  "workflow_run_steps",
  {
    id: serial("id").primaryKey(),
    runId: integer("run_id").notNull(),
    nodeId: varchar("node_id", { length: 64 }).notNull(),
    actionId: varchar("action_id", { length: 64 }),
    status: varchar("status", { length: 20 }).notNull().default("pending"), // pending | running | completed | failed | skipped | waiting
    scheduledFor: timestamp("scheduled_for"),
    startedAt: timestamp("started_at"),
    completedAt: timestamp("completed_at"),
    input: jsonb("input"),
    output: jsonb("output"),
    error: text("error"),
  },
  (t) => ({
    runIdx: index("idx_workflow_run_steps_run_id").on(t.runId),
    dueIdx: index("idx_workflow_run_steps_due").on(t.status, t.scheduledFor),
  })
);

export type WorkflowRunStep = typeof workflowRunSteps.$inferSelect;
